import { Loader2Icon } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { CopyCommand } from "./copy-command";

// First-run / not-connected state. The lab runs on the member's machine, so we
// walk them through the one-time CLI install, auth, and `lab start`. The page
// polls for the session and swaps to the live view automatically once connected.
export function InstallCard({ slug }: { slug: string }) {
  const steps = [
    {
      label: "Install the lab CLI",
      cmd: "curl -fsSL https://raw.githubusercontent.com/Hodik/lab-releases/main/install.sh | sh",
      note: "macOS and Linux · installs to ~/.local/bin",
    },
    { label: "Authenticate (opens your browser once)", cmd: "lab login" },
    {
      label: "Start this lab",
      cmd: `lab start ${slug}`,
      note: "The lab builds its own disposable environment.",
    },
  ];

  return (
    <Card className="max-w-2xl">
      <CardHeader>
        <CardTitle>Set up once, then you're done forever</CardTitle>
        <CardDescription>
          The lab runs on your machine. Install the CLI once, then start any lab
          with one command. It can only touch the practice environment it creates.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {steps.map((step, i) => (
          <div key={i} className="flex gap-3">
            <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold">
              {i + 1}
            </span>
            <div className="min-w-0 flex-1 space-y-2">
              <p className="text-sm font-medium">{step.label}</p>
              <CopyCommand cmd={step.cmd} />
              {step.note && (
                <p className="text-xs text-muted-foreground">{step.note}</p>
              )}
            </div>
          </div>
        ))}
        <div className="flex items-center gap-2 border-t pt-4 text-sm text-muted-foreground">
          <Loader2Icon className="size-4 animate-spin" />
          Waiting for your terminal — this page connects automatically the moment
          the lab starts.
        </div>
      </CardContent>
    </Card>
  );
}
